import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Collapse, Box } from '@mui/material';

/*
 * AirspaceCard normalises the output of controlled airspace checks.  It
 * consistently returns one of the defined severities ('go', 'advisory',
 * 'caution', 'no-go') via onUpdate() and displays a consistent summary
 * line.  See STATUS_MESSAGES below for the mapping.
 */
 

const STATUS_MESSAGES = {
  go: '✅ Clear for Takeoff',
  advisory: 'ℹ️ Clear With Advisories',
  caution: '⚠️ Flight Not Recommended',
  'no-go': '❌ Do Not Fly',
  unknown: 'ℹ️ Clear With Advisories'
};

const AirspaceCard = ({ lat, lon, refreshTrigger, onUpdate }) => {
  const [summaryText, setSummaryText] = useState('Airspace data not loaded.');
  const [detailText, setDetailText] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [severity, setSeverity] = useState('unknown');

  useEffect(() => {
    const fetchAirspace = async () => {
      if (!lat || !lon) {
        // Without a location we cannot query airspace.  Report as advisory.
        setSeverity('advisory');
        setSummaryText(STATUS_MESSAGES['advisory']);
        setDetailText('Airspace info not available. Click "Use My Location" first.');
        onUpdate && onUpdate('advisory', ['No location set']);
        return;
      }

      try {
		  console.debug('🔁 AirspaceCard running fetch with:', { lat, lon, refreshTrigger });

        const res = await fetch(`https://flightiq.app/API/check-airspacev2.php?lat=${lat}&lon=${lon}&radius=10000&type=asp`);

        const data = await res.json();
        const results = Array.isArray(data.airspace) ? data.airspace : [];

        if (results.length === 0) {
          // No controlled airspace within the query radius
          const sev = 'go';
          setSeverity(sev);
          setSummaryText(STATUS_MESSAGES[sev]);
          setDetailText('No controlled airspace within 3 miles.');
          onUpdate && onUpdate(sev, []);
          return;
        }

        const parsed = results
          .map(r => ({
            name: r.name,
            category: r.category,
            alt: parseInt(r.alt_bottom, 10),
            alt_type: r.alt_type || 'AGL'
          }))
          .filter(x => !isNaN(x.alt))
          .sort((a, b) => a.alt - b.alt);

        const nearest = parsed[0];
        let sev = 'go';
        let highlight = '';

        if (nearest.alt_type === 'AGL' && nearest.alt <= 400) {
          // Inside or very near controlled airspace – do not fly
          sev = 'no-go';
          highlight = `❌ ${nearest.category} airspace (${nearest.name}) starts at ${nearest.alt} ft AGL.`;
        } else if (nearest.alt_type === 'AGL' && nearest.alt <= 700) {
          // Within a caution zone around controlled airspace
          sev = 'caution';
          highlight = `⚠️ ${nearest.category} airspace (${nearest.name}) starts at ${nearest.alt} ft AGL.`;
        } else {
          highlight = `✅ Closest airspace: ${nearest.category} (${nearest.name}) at ${nearest.alt} ft ${nearest.alt_type}.`;
        }

        // Detail block with all zones
        const html = parsed.map((zone, idx) => {
          const tag = zone.alt <= 400 && zone.alt_type === 'AGL'
            ? '<strong style="color:red">NO-GO</strong>'
            : zone.alt <= 700 && zone.alt_type === 'AGL'
            ? '<strong style="color:orange">CAUTION</strong>'
            : '';
          return `
            <div>
              ${idx + 1}. ${tag} <strong>${zone.category}</strong> – ${zone.name}<br/>
              Starts at ${zone.alt} ft ${zone.alt_type}
            </div><br/>
          `;
        }).join('');

        // Use the common status messages for summary; highlight goes into detail.
        setSeverity(sev);
        setSummaryText(STATUS_MESSAGES[sev] || STATUS_MESSAGES['unknown']);
        setDetailText(`<strong>${highlight}</strong><br/><br/>` + html);
        // Provide a reason array when not GO
        const reasons = sev === 'go' ? [] : [highlight.replace(/✅ |⚠️ |❌ /g, '').trim()];
        onUpdate && onUpdate(sev, reasons);
        console.debug('AirspaceCard: severity', sev, 'reasons', reasons);
      } catch (err) {
        console.error('Airspace fetch error:', err);
        // On error, return an advisory status.  The error may be due to
        // connectivity or API issues.
        setSeverity('advisory');
        setSummaryText(STATUS_MESSAGES['advisory']);
        setDetailText('Airspace information unavailable.');
        onUpdate && onUpdate('advisory', ['Airspace data unavailable']);
      }
    };

    fetchAirspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, refreshTrigger]);

  const borderColor = severity === 'no-go' ? 'red'
                    : severity === 'caution' ? 'orange'
                    : severity === 'go' ? 'green'
                    : 'gray';

  return (
    <Card 
      onClick={() => setExpanded(!expanded)} 
      sx={{ cursor: 'pointer', borderLeft: `6px solid ${borderColor}`, mb: 1 }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Airspace</Typography>
          <Typography variant="body1" sx={{ opacity: 0.7 }}>🛫</Typography>
        </Box>
        <Typography variant="subtitle1" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: summaryText }} />
        <Collapse in={expanded}>
          <Typography variant="body2" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: detailText }} />
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default AirspaceCard;
